/**
 * La libreta del inventario (INV-01): cada cambio de stock, con su motivo.
 *
 * La usan la pantalla de inventario (todos los productos) y el detalle de un
 * producto (solo los suyos), que por eso puede esconder la columna del producto.
 */
import { Link } from 'react-router-dom';

import type { MovimientoStock, TipoMovimientoStock } from '@bandu/compartido';

import { fechaHora, pesos } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';
import { Tabla } from './Tabla';

const TIPO: Record<TipoMovimientoStock, { clase: string; texto: string }> = {
  ingreso: { clase: 'etq etq-ok', texto: 'Llegó mercadería' },
  venta_web: { clase: 'etq etq-info', texto: 'Venta del sitio' },
  venta_externa: { clase: 'etq etq-wa', texto: 'Venta por fuera' },
  ajuste: { clase: 'etq etq-neutra', texto: 'Conteo o ajuste' },
};

export interface TablaMovimientosProps {
  movimientos: MovimientoStock[];
  /** El detalle de un producto no necesita repetir su nombre en cada fila. */
  sinProducto?: boolean;
}

function detalleDe(movimiento: MovimientoStock): string {
  const partes = [
    movimiento.referenciaPedido !== null ? `Pedido ${movimiento.referenciaPedido}` : null,
    movimiento.montoTotal !== null ? pesos(movimiento.montoTotal) : null,
    movimiento.nota,
    movimiento.administradorNombre !== null ? `por ${movimiento.administradorNombre}` : null,
  ];

  return partes.filter((parte) => parte !== null).join(' · ');
}

export function TablaMovimientos({ movimientos, sinProducto = false }: TablaMovimientosProps) {
  if (movimientos.length === 0) {
    return <p className="panel-vacio">Todavía no hay movimientos.</p>;
  }

  return (
    <Tabla
      titulo="Movimientos de inventario"
      encabezados={
        <>
          <th scope="col">Fecha</th>
          {sinProducto ? null : <th scope="col">Producto</th>}
          <th scope="col">Motivo</th>
          <th scope="col" className="der">
            Unidades
          </th>
          <th scope="col" className="der">
            Quedó en
          </th>
          <th scope="col">Detalle</th>
        </>
      }
    >
      {movimientos.map((movimiento) => {
        const tipo = TIPO[movimiento.tipo];
        const entra = movimiento.cantidad > 0;

        return (
          <tr key={movimiento.id}>
            <th scope="row">
              <time dateTime={movimiento.creadoEn}>{fechaHora(movimiento.creadoEn)}</time>
            </th>
            {sinProducto ? null : (
              <td>
                {movimiento.productoId !== null ? (
                  <Link to={rutas.administracion.productoDetalle(movimiento.productoId)}>
                    {movimiento.nombreProducto}
                  </Link>
                ) : (
                  // El producto se elimino despues: la historia se conserva igual.
                  movimiento.nombreProducto
                )}
                <span className="s mono">{movimiento.sku}</span>
              </td>
            )}
            <td>
              <span className={tipo.clase}>{tipo.texto}</span>
            </td>
            <td className={entra ? 'der mov-cant entra' : 'der mov-cant sale'}>
              {entra ? '+' : '−'}
              {Math.abs(movimiento.cantidad)}
            </td>
            <td className="der num">{movimiento.stockResultante}</td>
            <td>{detalleDe(movimiento)}</td>
          </tr>
        );
      })}
    </Tabla>
  );
}
